import React from "react";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import { Box, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import { AddCircleOutlineOutlined } from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { deleteRequest, getRequest } from "Includes/api/apicall";
import { DEL_URL, GET_URL } from "Includes/urls";

const serviceLabels = {
  erp: "ERP",
  otherService: "Other service",
  idCard: "ID card",
  biometric: "Biometric",
};

function QuotationPlanList(props) {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });

  const getQuotationPlanList = React.useCallback(() => {
    setLoading(true);
    getRequest(
      GET_URL.quotation_plans.api,
      {
        limit: paginationModel.pageSize,
        pageno: paginationModel.page + 1,
      },
      props
    )
      .then((res) => {
        if (res?.status === 200) {
          const plans = res.data?.data || [];
          setRows(plans);
          setRowCount(res.data?.count || plans.length);
        }
      })
      .finally(() => setLoading(false));
  }, [paginationModel.page, paginationModel.pageSize, props]);

  React.useEffect(() => {
    getQuotationPlanList();
  }, [getQuotationPlanList]);

  const handleDelete = (id) => {
    deleteRequest(`${DEL_URL.quotation_plans.api}${id}/`, {}, props).then((response) => {
      if (response?.status === 200) {
        Swal.fire("Deleted!", "Quotation Plan deleted.", "success");
        getQuotationPlanList();
      }
    });
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "name",
      headerName: "Plan Name",
      flex: 1,
      minWidth: 180,
    },
    {
      field: "service_toggles",
      headerName: "Services",
      flex: 1.4,
      minWidth: 260,
      renderCell: (params) => {
        const enabled = Object.entries(params.value || {}).filter(([, value]) => !!value);
        if (!enabled.length) return "-";
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {enabled.map(([key]) => (
              <Chip key={key} label={serviceLabels[key] || key} size="small" variant="outlined" />
            ))}
          </Box>
        );
      },
    },
    {
      field: "amount_per_student",
      headerName: "ERP Amount",
      width: 140,
      renderCell: (params) =>
        params.value ? `Rs. ${parseFloat(params.value || 0).toLocaleString("en-IN")}` : "-",
    },
    {
      field: "per_id_amount",
      headerName: "ID Card",
      width: 120,
      renderCell: (params) =>
        params.value ? `Rs. ${parseFloat(params.value || 0).toLocaleString("en-IN")}` : "-",
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          label="Edit"
          onClick={() => navigate(`/quotation/plan?id=${params.row.id}`)}
          showInMenu
        />,
        <GridActionsCellItem
          key="delete"
          label="Delete"
          onClick={() => handleDelete(params.row.id)}
          showInMenu
        />,
      ],
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Grid container alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="h5" fontWeight={600}>
            Quotation Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage reusable quotation plan templates
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} textAlign="right">
          <Button
            variant="contained"
            component={Link}
            to="/quotation/plan"
            startIcon={<AddCircleOutlineOutlined />}
            sx={{ borderRadius: 2, textTransform: "none", px: 2, py: 1 }}
          >
            Add Quotation Plan
          </Button>
        </Grid>
      </Grid>

      <Paper sx={{ mt: 3, p: 2, borderRadius: 3 }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          rowCount={rowCount}
          autoHeight
          rowHeight={56}
          disableSelectionOnClick
          sx={{ border: "none" }}
        />
      </Paper>
    </Box>
  );
}

export default QuotationPlanList;
